import React from 'react';
import PropTypes from 'prop-types';
import { r_spinner_animated } from '../../../icons';
import getRenderable from '../../../lib/getRenderable';
import Icon from '../Icon';
import {
    COLORS,
    TEXT_COLORS,
    SIZES,
    FILLS
} from './constants';
import generateClasses from './generateClasses';
import './index.scss';

const Button = ({
    children,
    href,
    disabled,
    color,
    textColor,
    fill,
    className,
    size,
    innerRef,
    loading,
    ...rest
}) => {
    const Element = href ? 'a' : 'button';
    const classes = generateClasses({
        color,
        fill,
        size,
        className,
        disabled,
        loading,
        textColor
    });
    const extendedProps = {
        ...rest,
        ref: innerRef,
        href
    };

    return (
        <Element className={classes.main}
            disabled={disabled}
            {...extendedProps}>
            {getRenderable(children)}
            { loading && <Icon className={classes.icon}>{r_spinner_animated}</Icon> }
        </Element>
    );
};

Button.propTypes = {
    /**
     * Any node that can be rendered.
     */
    children: PropTypes.node.isRequired,
    /**
     * A URL this button directs to.
     * For more infromation: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-href
     */
    href: PropTypes.string,
    /**
     * Whether this button is disabled or not.
     */
    disabled: PropTypes.bool,
    /**
     * The button's color.
     * Supported options are exposed through the static `COLORS` property.
     */
    color: PropTypes.oneOf(Object.values(COLORS)),
    /**
     * The button's text color, relevant **only for `WHITE` buttons.**
     * Supported options are exposed through the static `TEXT_COLORS` property.
     */
    textColor: PropTypes.oneOf(Object.values(TEXT_COLORS)),
    /**
     * The button's fill option.
     * Supported options are exposed through the static `FILLS` property.
     */
    fill: PropTypes.oneOf(Object.values(FILLS)),
    /**
     * The button's size.
     * Supported options are exposed through the static `SIZES` property.
     */
    size: PropTypes.oneOf(Object.values(SIZES)),
    /**
     * Provides a React `ref` access to the component's `<button>` or `<a>` element.
     */
    innerRef: PropTypes.func,
    /**
     * Displays a loading state.
     */
    loading: PropTypes.bool,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string
};

Button.defaultProps = {
    disabled: false,
    color: COLORS.GREEN,
    textColor: TEXT_COLORS.GREEN,
    fill: FILLS.FULL,
    size: SIZES.MEDIUM,
    loading: false
};

Object.assign(Button, {
    COLORS,
    TEXT_COLORS,
    FILLS,
    SIZES
});

export default Button;
